// X3 — how the canonicaliser packed a corpus's vocabulary. Corpus contains
// concepts; a concept contains the document definitions that merged into it.
//
// THREE FACTS ABOUT THIS PAYLOAD THAT COST TIME IF ASSUMED.
//
//  1. **`is_singleton` is not on a `CorpusEntity`.** The reconciler drops it
//     when it builds one, so what this chart shades is `member_refs.length
//     === 1` — "the canonicaliser packed nothing into this". Say it that way
//     and not by borrowing the field's name, because the name is doubly
//     booby-trapped: `Cluster.is_singleton` (a cluster of one) and
//     `GlossaryEntry.is_singleton` (a term used once in its own prose) are
//     unrelated concepts sharing it.
//  2. **`surface` does not exist.** The canonical surface is `name`.
//  3. **`confidence` can exceed 1.** Exact-name clusters come back as
//     1.0000000000000002 from float error, so it is clamped before anything
//     proportional is drawn from it.
//
// Singletons are 63% of the seeded corpus. That is the GROUND STATE, not an
// exception, which is why they are drawn rather than filtered out — a packing
// view that hid them would show a corpus far more connected than it is.

import { Card, EmptyState } from "../../ui";
import { Pack, type PackNode } from "../../charts";
import type { CorpusGlossary } from "./api";
import { formatCount, shownOf } from "./format";
import {
  PACK_EMPTY_BODY,
  PACK_EMPTY_HEADLINE,
  PACK_SUBTITLE,
  PACK_TITLE,
} from "./metricLabels";
import "./analytics.css";

const MAX_CONCEPTS = 120;

/** Glossary -> pack nodes, widest first. Exported for the test: the singleton
 *  rule and the ordering are the contract, and asserting them through rendered
 *  circles would pin the drawing instead. */
export function packNodes(
  glossary: CorpusGlossary,
  limit = MAX_CONCEPTS,
): { nodes: PackNode[]; total: number; singletons: number } {
  const terms = glossary.entities.filter(
    (entity) => entity.kind === "term" && !entity.is_orphan,
  );
  const nodes = terms
    .map((entity) => ({
      key: entity.entity_id,
      label: entity.name,
      confidence:
        entity.confidence === null ? null : Math.min(1, entity.confidence),
      children: entity.member_refs.map((ref) => ({
        key: ref,
        // The document half of `<doc_id>:<term_id>` — never split and rejoin
        // on the tail, which is document-local.
        label: ref.split(":", 1)[0],
      })),
    }))
    .sort((a, b) => b.children.length - a.children.length || a.label.localeCompare(b.label));

  return {
    nodes: nodes.slice(0, limit),
    total: nodes.length,
    singletons: nodes.filter((node) => node.children.length <= 1).length,
  };
}

export type CorpusPackingProps = {
  glossary: CorpusGlossary | null;
  onSelect?: (entityId: string) => void;
  selected?: string | null;
};

export function CorpusPacking({
  glossary,
  onSelect,
  selected,
}: CorpusPackingProps) {
  // `null` is "Pass 2 has never run for this corpus" — not an empty corpus.
  if (glossary === null) {
    return (
      <Card header={PACK_TITLE}>
        <EmptyState headline={PACK_EMPTY_HEADLINE} body={PACK_EMPTY_BODY} />
      </Card>
    );
  }

  const { nodes, total, singletons } = packNodes(glossary);

  if (nodes.length === 0) {
    return (
      <Card header={PACK_TITLE}>
        <EmptyState headline={PACK_EMPTY_HEADLINE} body={PACK_EMPTY_BODY} />
      </Card>
    );
  }

  const members = nodes.reduce((sum, node) => sum + node.children.length, 0);
  // Counted over the DRAWN slice, not the corpus. `singletons` from `packNodes`
  // is corpus-wide, and the label's "of them" refers to the concepts actually
  // on screen — mixing the two scopes in one sentence reads "120 concepts ...
  // 380 of them merged nothing" the moment a corpus exceeds the cap. Singletons
  // sort last (fewest children), so on a capped corpus the two numbers diverge
  // as far as they can.
  const drawnSingletons = nodes.filter((node) => node.children.length <= 1).length;

  return (
    <Card header={PACK_TITLE}>
      <p className="type-bodyS maisight-analytics-muted">{PACK_SUBTITLE}</p>
      <Pack
        label={
          `${formatCount(nodes.length)} canonical concepts holding` +
          ` ${formatCount(members)} document definitions;` +
          ` ${formatCount(drawnSingletons)} of them merged nothing`
        }
        nodes={nodes}
        onSelect={onSelect}
        selected={selected ?? null}
      />
      <div className="maisight-analytics-card-footer type-bodyS">
        <span>{shownOf(nodes.length, total, "concept")}</span>
        <span className="maisight-analytics-muted">
          {`${formatCount(singletons)} of ${formatCount(total)} held a single definition`}
        </span>
      </div>
    </Card>
  );
}
